import { CommonFilterModel, CommonParams } from '@/api/resources/common/shared/models/common.models'
import { AgGridColumnType, AgGridExportType, ExportedColDef, ExportedColGroupDef } from '@/shared/models/ag-grid.model'
import i18n from '@/lang/i18n'
import { ScientificAnalysisDatasource } from '@/modules/scientific-analysis-module/components/scientific-analysis-results-panel/datasources/scientific-analysis-datasource'
import { scientificAnalysisService } from '@/modules/scientific-analysis-module/services/scientific-analysis-services'
import { Nullable } from '@/shared/types/app.types'
import { ColDef } from '@ag-grid-enterprise/all-modules'
import { InfinitePaginatorResponse } from '@/shared/resources/infinite-datasources/infinite-resource.model'
import { ScientificAnalysisEntityComplementary } from '@/modules/scientific-analysis-module/services/scientific-analysis-dto-complementary'
import CellGridRichText from '@/components/common/CellGridRichText.vue'
import { ICellRendererParams } from '@ag-grid-community/core/dist/cjs/rendering/cellRenderers/iCellRenderer'
import { ScientificAnalysisCaseDatasource } from '@/modules/scientific-analysis-module/components/scientific-analysis-results-panel/datasources/scientific-analysis-datasource-case'
import { ScientificAnalysisPatientDatasource } from '@/modules/scientific-analysis-module/components/scientific-analysis-results-panel/datasources/scientific-analysis-datasource-patient'

export class ScientificAnalysisComplementaryDatasource extends ScientificAnalysisDatasource<ScientificAnalysisEntityComplementary> {
    public static getEntityGridColumns(selector: string = '', hideAll?:boolean): (ColDef | ExportedColDef | ExportedColGroupDef) {
        return {
            headerName: i18n.tc(`scientificAnalysis.grid.results.complementary.header`),
            children: [
                {
                    type: AgGridColumnType.TEXT,
                    headerName: i18n.tc(`scientificAnalysis.grid.results.complementary.caseNumber`),
                    exportField: `${selector}caseStudy.caseNumber`,
                    exportType: AgGridExportType.TEXT,
                    field: `${selector}caseStudy.caseNumber`,
                    sortable: false,
                    filter: false,
                    minWidth: 200,
                    flex: 1,
                    hide: hideAll
                },
                {
                    type: AgGridColumnType.TEXT,
                    headerName: i18n.tc(`scientificAnalysis.grid.results.complementary.responsible`),
                    exportField: `${selector}responsible.fullName`,
                    exportType: AgGridExportType.TEXT,
                    field: `${selector}responsible.fullName`,
                    sortable: false,
                    filter: false,
                    flex: 1,
                    minWidth: 200,
                    hide: hideAll
                },
                {
                    type: AgGridColumnType.TEXT,
                    headerName: i18n.tc(`scientificAnalysis.grid.results.complementary.pathologist`),
                    exportField: `${selector}pathologist.fullName`,
                    exportType: AgGridExportType.TEXT,
                    field: `${selector}pathologist.fullName`,
                    sortable: false,
                    filter: false,
                    flex: 1,
                    minWidth: 200,
                    hide: hideAll
                },
                {
                    type: AgGridColumnType.DATETIME,
                    headerName: i18n.tc(`scientificAnalysis.grid.results.complementary.validationDate`),
                    exportField: `${selector}validationDate`,
                    exportType: AgGridExportType.DATETIME,
                    field: `${selector}validationDate`,
                    sortable: false,
                    filter: false,
                    minWidth: 200,
                    flex: 1,
                    hide: hideAll
                },
                {
                    type: AgGridColumnType.TEXT,
                    headerName: i18n.tc(`scientificAnalysis.grid.results.complementary.complementaryInformation`),
                    exportField: `${selector}complementaryInformation.text`,
                    exportType: AgGridExportType.TEXT,
                    field: `${selector}complementaryInformation.text`,
                    sortable: false,
                    filter: false,
                    minWidth: 200,
                    width: 500,
                    hide: hideAll,
                    cellStyle: { 'white-space': 'normal', 'overflow-y': 'auto' },
                    cellRendererFramework: CellGridRichText
                },
                {
                    type: AgGridColumnType.TEXT,
                    headerName: i18n.tc(`scientificAnalysis.grid.results.complementary.complementaryCodification`),
                    field: `${selector}complementaryCodification`,
                    exportDisabled: true,
                    sortable: false,
                    filter: false,
                    flex: 1,
                    minWidth: 200,
                    hide: hideAll,
                    cellRenderer: ({ data }: ICellRendererParams) => {
                        let value = ``
                        if (data && data.complementaryCodification && data.complementaryCodification.length > 0) {
                            for (const codification of data.complementaryCodification) {
                                if (codification.topographicCode) {
                                    if (value.length > 0) {
                                        value += ` ,`
                                    }
                                    value += codification.topographicCode.code + `|` + codification.topographicCode.description
                                }
                                if (codification.morphologicCode) {
                                    if (value.length > 0) {
                                        value += ` ,`
                                    }
                                    value += codification.morphologicCode.code + `|` + codification.morphologicCode.description
                                }
                            }
                        }
                        return value
                    }
                },
                {
                    type: AgGridColumnType.CHECK_ICON,
                    headerName: i18n.tc(`scientificAnalysis.grid.results.complementary.isSampleComplementary`),
                    exportField: `${selector}isSampleComplementary`,
                    exportType: AgGridExportType.BOOLEAN,
                    field: `${selector}isSampleComplementary`,
                    sortable: false,
                    filter: false,
                    flex: 1,
                    minWidth: 200,
                    hide: true
                },
                {
                    type: AgGridColumnType.CHECK_ICON,
                    headerName: i18n.tc(`scientificAnalysis.grid.results.complementary.hasAttachments`),
                    exportField: `${selector}hasAttachments`,
                    exportType: AgGridExportType.BOOLEAN,
                    field: `${selector}hasAttachments`,
                    sortable: false,
                    filter: false,
                    flex: 1,
                    minWidth: 200,
                    hide: true
                }
            ]

        }
    }

    public getGridColumns(): (ColDef | ExportedColDef | ExportedColGroupDef)[] {
        return [
            { type: AgGridColumnType.CUSTOM_CHECKBOX },
            ScientificAnalysisComplementaryDatasource.getEntityGridColumns(),
            ScientificAnalysisCaseDatasource.getEntityGridColumns(`caseStudy.`, true),
            ScientificAnalysisPatientDatasource.getEntityGridColumns(`caseStudy.patient.`, true)
        ]
    }

    public async paginator(_filterModel: CommonFilterModel, params: CommonParams, scrollId: string, totalElements: number): Promise<InfinitePaginatorResponse<ScientificAnalysisEntityComplementary>> {
        const response: InfinitePaginatorResponse<ScientificAnalysisEntityComplementary> = await scientificAnalysisService.search(this.scientificAnalysis, params, scrollId, totalElements) as InfinitePaginatorResponse<ScientificAnalysisEntityComplementary>
        this.setTotalElements(response.scrollPage.totalElements)
        return {
            content: response.content,
            scrollPage: {
                scrollId: encodeURIComponent(JSON.stringify(response?.scrollPage?.scrollId)),
                size: response.scrollPage.size,
                totalElements: response.scrollPage.totalElements
            }
        }
    }

    public getPatAndLabId(selectedRow: ScientificAnalysisEntityComplementary): [Nullable<number>, Nullable<number>] {
        return [selectedRow.caseStudy.patient.id, selectedRow.caseStudy.laboratoryId]
    }

    public getLabAndCaseId(selectedRow: ScientificAnalysisEntityComplementary): [Nullable<number>, Nullable<number>] {
        return [selectedRow.caseStudy.laboratoryId, selectedRow.caseStudy.id]
    }

    public getHTMLFieldsList(data: ScientificAnalysisEntityComplementary): string[] {
        const fields = [data.complementaryInformation.text]
        return fields.filter(fieldValue => fieldValue)
    }
}
